import React, { ChangeEvent, FC, useMemo, useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
// Images
import LogoImage from 'assets/newLogos/upper-left-corner.png';
// Core
import { ILiveScreenInfo } from 'redux/dashboard-service/types';
// Redux
import { useSelector } from 'react-redux';
import { selectorGetLiveScreen } from 'redux/dashboard-service/selector';
// Formik
import { Field, Form, useFormikContext } from 'formik';
// Components
import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import { MediaSlider, MediaItem } from 'shared/components/MediaSlider';
// import Input from 'shared/components/Inputs/Input';
// Styles
import cx from 'classnames';
import { formatInteger } from 'core/functions/formatNumber';
import styles from './LiveScreenForm.module.scss';

interface LiveScreenFormProps {
	editMode: boolean;
	onCancelEditMode: () => void;
	// eslint-disable-next-line no-unused-vars
	onSaveChanges: (values: Omit<ILiveScreenInfo, 'mediaPathes'> & { mediaPathes: (string | File)[] }) => Promise<void>;
}

export const LiveScreenForm: FC<LiveScreenFormProps> = ({ editMode, onCancelEditMode, onSaveChanges }) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const { dirty, isValid, values, resetForm, setFieldValue, errors, touched } = useFormikContext<any>();
	const liveScreenInfo = useSelector(selectorGetLiveScreen);

	const mediaItems: MediaItem[] = (values.mediaPathes || []) as MediaItem[];

	const [showError, setShowError] = useState(false);
	const mediaError = touched.mediaPathes && errors.mediaPathes;

	const handleOpenMedia = () => {
		if (inputRef.current) inputRef.current.click();
	};

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		const selectedFile = e.target.files?.[0];
		if (!selectedFile) {
			return;
		}

		// Allow image files and MP4 video files
		const isImage = selectedFile.type.startsWith('image/');
		const isVideo = selectedFile.type === 'video/mp4' || selectedFile.name.toLowerCase().endsWith('.mp4');

		if (!isImage && !isVideo) {
			setShowError(true);
			return;
		}

		// Check for file size (not larger than 10 MB)
		if (selectedFile.size > 10 * 1024 * 1024) {
			setShowError(true);
			return;
		}

		setShowError(false);
		// Replace existing media with new file (only one media allowed)
		setFieldValue('mediaPathes', [selectedFile]);
		e.target.value = ''; // Reset input
	};

	const handleRemoveMedia = (index: number) => {
		// Remove media item using Formik
		const updatedMedia = mediaItems.filter((_, i) => i !== index);
		setFieldValue('mediaPathes', updatedMedia);
		// Trigger validation error if no media left
		if (updatedMedia.length === 0) {
			setFieldValue('mediaPathes', []);
		}
	};

	const onCancelEdit = () => {
		resetForm();
		setShowError(false);
		onCancelEditMode();
	};

	const convertedTime = useMemo(() => {
		return liveScreenInfo?.spendTime ? Math.round(liveScreenInfo.spendTime / 3600) : '';
	}, [liveScreenInfo?.spendTime]);

	const handleChangeFieldValue = (event: ChangeEvent<HTMLTextAreaElement>, key: string) => {
		setFieldValue(key, event.target.value).then();
	};

	return (
		<Form>
			<div className={styles.container}>
				<div className={styles.board}>
					<div className={styles.top}>
						<div className={styles.top__organization}>
							<img className={styles.top__organization__avatar} src={LogoImage} alt="avatar" />
						</div>
						<div
							className={cx(styles.top__title_wrapper, {
								[styles.top__title_edit]: editMode,
							})}
						>
							<Field
								className={styles.top__title}
								name="title"
								id="title"
								value={values.title}
								disabled={!editMode}
								onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
									handleChangeFieldValue(event, 'title')
								}
								component={TextareaAutosize}
							/>
						</div>
					</div>

					<div className={cx(styles.card, styles.card_first)}>
						<span className={styles.card__title}>World wide hours</span>
						<span className={styles.card__count}>
							{convertedTime === '' ? '' : formatInteger(convertedTime)}
						</span>
					</div>

					<div className={cx(styles.card, styles.card_second)}>
						<span className={styles.card__title}>Total Opportunities</span>
						<span className={styles.card__count}>{formatInteger(liveScreenInfo?.opportunitiesCount)}</span>
					</div>

					<div className={cx(styles.card, styles.card_third)}>
						<span className={styles.card__title}>Volunteer spotlight</span>
						{liveScreenInfo?.recentlyVolunteerName ? (
							<div className={styles.card__volunteer}>
								<div className={styles.card__volunteer__avatar}>
									{liveScreenInfo?.recentlyVolunteerName.split('').join(' ')}
								</div>
							</div>
						) : null}
						{liveScreenInfo?.recentlyOppoName ? (
							<div
								className={cx(styles.card__subTitle, styles.recentlyOppo, {
									[styles.card__volunteer__oppo_padding]:
										liveScreenInfo?.recentlyVolunteerName.trim().length >= 43,
									[styles.card__volunteer__oppo]:
										liveScreenInfo?.recentlyOppoName.trim().length >= 38,
								})}
								title={liveScreenInfo?.recentlyOppoName}
							>
								<span className={styles.card__subTitle_bold}>Opportunity: </span>
								<span className={styles.card__opponame}>{liveScreenInfo?.recentlyOppoName}</span>
							</div>
						) : null}
					</div>

					<div
						className={cx(styles.bottom, {
							[styles.bottom_edit]: editMode,
						})}
					>
						<img src={LogoImage} className={styles.bottom__avatar} alt="logo" />
						<Field
							className={cx(styles.bottom__subTitle, {
								[styles.bottom__subTitle_edit]: editMode,
							})}
							name="subTitle"
							id="subTitle"
							value={values.subTitle}
							disabled={!editMode}
							onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
								handleChangeFieldValue(event, 'subTitle')
							}
							component={TextareaAutosize}
						/>
					</div>
					<div className={styles.media}>
						{showError && (
							<span className={styles.error}>
								Please choose an image (PNG, GIF, JPEG) or MP4 video file smaller than 10 MB.
							</span>
						)}
						{mediaError && (
							<span className={styles.error}>
								{typeof mediaError === 'string' ? mediaError : 'Media is required'}
							</span>
						)}

						<div className={styles.media__slider_wrapper}>
							{editMode && mediaItems.length > 0 && (
								<div className={styles.media__upload_overlay} onClick={handleOpenMedia}>
									<span className={styles.media__upload_text}>Upload New Media</span>
								</div>
							)}
							<MediaSlider
								mediaItems={mediaItems}
								editMode={editMode}
								autoplayDelay={values.interval || 10}
								showUploadSlide={editMode && mediaItems.length === 0}
								onUploadClick={handleOpenMedia}
								onRemoveMedia={handleRemoveMedia}
								className={styles.media__slider}
							/>
						</div>
					</div>
					{/* {editMode && (
						<div className={styles.media__controls}>
							<span className={styles.media__interval_label}>Interval</span>
							<Field
								name="interval"
								component={Input}
								type="number"
								isMarginLeftUnset
								isSmallHeight
								isDisableHeight
								min={1}
								parentClassName={styles.media__interval_input}
							/>
							<span className={styles.media__interval_label}>seconds</span>
						</div>
					)} */}
					{editMode && (
						<div className={styles.button}>
							<ButtonDefault
								submitType={true}
								classList={['primary', 'md']}
								parentClassName={
									(!dirty || !isValid) && styles.disable_submit ? styles.disable_submit : ''
								}
								title="Save"
								disabled={!dirty || !isValid}
								clickHandler={() => onSaveChanges(values)}
							/>
							<ButtonDefault
								classList={['secondary', 'md']}
								parentClassName={styles.cancel_button}
								title="Cancel"
								clickHandler={onCancelEdit}
							/>
						</div>
					)}
				</div>
			</div>
			<input
				onChange={handleFileChange}
				accept="video/mp4,image/jpeg,image/png,image/bmp"
				ref={inputRef}
				className={styles.input}
				type="file"
			/>
		</Form>
	);
};
